import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ArrowUp, Check, Copy, CornerDownRight, Crosshair, Eraser, RotateCcw, Sparkles, Square, X } from 'lucide-react';
import { IconButton } from '../../../components/ui';
import { askIntelligence } from '../intelligenceApi';
import { AskRequest, IntelligenceDomain, ReferencesResult } from '../types';
import { StreamStatus, useIntelligenceStream } from '../useIntelligenceStream';
import { IntelText } from './IntelText';
import { AiNotice, StreamProgress, plainText, useCopy } from './BriefPanel';

export interface AskPanelProps {
  domain: IntelligenceDomain;
  /** Suggested questions, generated from analytics by the page */
  suggestions: string[];
  /** Client-computed analytics digest (≤ 12,000 chars) */
  buildContext: () => string;
  /** Entities the analyst is looking at (selection, cluster, path) */
  focusIds?: string[];
  onEntity?: (id: string) => void;
  onEntityHover?: (id: string | null) => void;
  entityColor?: (id: string) => string | undefined;
  /** Entities referenced by the answer in view (empty when cleared) */
  onReferences?: (ids: string[]) => void;
  onClose?: () => void;
  /** When given, answers show an action that fits the referenced entities (e.g. "Show on map") */
  onShowReferences?: (ids: string[]) => void;
  showReferencesLabel?: string;
  title?: string;
  placeholder?: string;
  /** Short scope line under the title, e.g. "Focused on Amara Diallo" */
  scopeLabel?: string;
  autoFocus?: boolean;
  className?: string;
}

export interface AskPanelHandle {
  /** Asks a question as if typed */
  ask: (question: string) => void;
  focus: () => void;
}

export interface AskTurn {
  id: string;
  question: string;
  text: string;
  progress: string[];
  status: StreamStatus;
  error: string | null;
  entityIds: string[];
}

const MAX_TURNS = 5;
const CONTEXT_LIMIT = 12000;

/** Conversation per domain, kept in memory for the session */
const memory = new Map<IntelligenceDomain, AskTurn[]>();

/** Test helper */
export const __resetAskMemory = () => memory.clear();

/**
 * Adds the last answers to the analytics digest so follow-up questions ("and her deputy?")
 * keep their meaning; the digest is trimmed first so the total stays within the limit.
 */
export function composeAskContext(digest: string, history: Array<Pick<AskTurn, 'question' | 'text'>>): string {
  const recent = history
    .filter((t) => t.text.trim())
    .slice(-2)
    .map((t) => `Q: ${t.question}\nA: ${plainText(t.text).slice(0, 900)}${t.text.length > 900 ? '…' : ''}`)
    .join('\n\n');
  if (!recent) return digest.slice(0, CONTEXT_LIMIT);
  const tail = `\n\nConversation so far:\n${recent}`;
  return digest.slice(0, Math.max(0, CONTEXT_LIMIT - tail.length)) + tail;
}

let turnSeq = 0;

/**
 * Conversational AI panel: suggested questions, streamed answers with entity chips,
 * referenced entities reported to the page for highlighting, last 5 turns remembered.
 */
export const AskPanel = forwardRef<AskPanelHandle, AskPanelProps>(
  (
    {
      domain,
      suggestions,
      buildContext,
      focusIds,
      onEntity,
      onEntityHover,
      entityColor,
      onReferences,
      onClose,
      onShowReferences,
      showReferencesLabel = 'Show on map',
      title = 'Ask',
      placeholder = 'Ask a question…',
      scopeLabel,
      autoFocus = false,
      className = '',
    },
    ref,
  ) => {
    const stream = useIntelligenceStream<AskRequest, ReferencesResult>(askIntelligence);
    const [turns, setTurns] = useState<AskTurn[]>(() => memory.get(domain) || []);
    const [draft, setDraft] = useState('');
    const activeId = useRef<string | null>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const onReferencesRef = useRef(onReferences);
    onReferencesRef.current = onReferences;
    const turnsRef = useRef(turns);
    turnsRef.current = turns;

    const running = stream.status === 'running';

    const commit = useCallback(
      (next: AskTurn[]) => {
        memory.set(domain, next);
        setTurns(next);
      },
      [domain],
    );

    const ask = useCallback(
      (raw: string) => {
        const question = raw.trim();
        if (!question) return;
        const id = `t${++turnSeq}`;
        // A question asked mid-answer replaces the running one
        const history = turnsRef.current.map((t) => (t.status === 'running' ? { ...t, status: 'done' as StreamStatus } : t));
        const turn: AskTurn = { id, question, text: '', progress: [], status: 'running', error: null, entityIds: [] };
        activeId.current = id;
        commit([...history, turn].slice(-MAX_TURNS));
        setDraft('');
        stream.start({
          domain,
          question,
          focusIds: focusIds?.length ? focusIds : undefined,
          context: composeAskContext(buildContext(), history),
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [domain, focusIds, buildContext, commit, stream.start],
    );

    useImperativeHandle(ref, () => ({ ask, focus: () => inputRef.current?.focus() }), [ask]);

    // Mirror the live stream into the active turn
    useEffect(() => {
      const id = activeId.current;
      if (!id) return;
      const entityIds = stream.result?.entityIds?.length ? stream.result.entityIds : stream.entityIds;
      const current = turnsRef.current;
      const index = current.findIndex((t) => t.id === id);
      if (index === -1) return;
      const status: StreamStatus = stream.status === 'idle' ? 'done' : stream.status;
      const next = current.slice();
      next[index] = { ...current[index], text: stream.text, progress: stream.progress, status, error: stream.error, entityIds };
      commit(next);
      if (status !== 'running') activeId.current = null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stream.status, stream.text, stream.progress, stream.error, stream.result, stream.entityIds]);

    // Report the entities of the latest answer to the page
    const last = turns[turns.length - 1];
    const lastIds = last?.entityIds.join(',') || '';
    useEffect(() => {
      onReferencesRef.current?.(last?.entityIds || []);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastIds]);

    // Keep the newest content in view while streaming
    useEffect(() => {
      const el = scrollRef.current;
      if (!turns.length) return;
      if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, [turns.length, last?.text.length]);

    useEffect(() => {
      if (autoFocus) window.setTimeout(() => inputRef.current?.focus(), 60);
    }, [autoFocus]);

    // Auto-grow the composer
    useEffect(() => {
      const el = inputRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(120, el.scrollHeight)}px`;
    }, [draft]);

    const clear = () => {
      stream.reset();
      activeId.current = null;
      commit([]);
    };

    const retry = (turn: AskTurn) => {
      commit(turnsRef.current.filter((t) => t.id !== turn.id));
      turnsRef.current = turnsRef.current.filter((t) => t.id !== turn.id);
      ask(turn.question);
    };

    return (
      <section aria-label={title} className={`flex min-h-0 min-w-0 flex-col ${className}`}>
        <header className="flex flex-shrink-0 items-center gap-2 px-4 pb-2 pt-3">
          <Sparkles className="h-4 w-4 flex-shrink-0 text-primary-600" strokeWidth={1.75} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[15px] leading-tight tracking-[-0.01em] text-ink">{title}</h2>
            {scopeLabel && <p className="truncate text-[12px] text-neutral-400">{scopeLabel}</p>}
          </div>
          {turns.length > 0 && (
            <IconButton label="Clear conversation" size="sm" onClick={clear}>
              <Eraser className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          )}
          {onClose && (
            <IconButton label="Close" size="sm" onClick={onClose} className="-mr-1.5">
              <X className="h-4 w-4" strokeWidth={1.75} />
            </IconButton>
          )}
        </header>

        <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-3">
          {turns.length === 0 && suggestions.length > 0 && (
            <div className="pt-1">
              <p className="mb-1.5 text-[12px] text-neutral-400">Suggested</p>
              <ul className="-mx-2 space-y-0.5">
                {suggestions.map((s) => (
                  <li key={s}>
                    <button
                      type="button"
                      onClick={() => ask(s)}
                      className="group flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left text-[13px] leading-snug text-neutral-700 transition-colors hover:bg-neutral-50 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
                    >
                      <CornerDownRight className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-neutral-300 transition-colors group-hover:text-primary-600" strokeWidth={1.75} />
                      <span className="min-w-0">{s}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ol className="space-y-5">
            {turns.map((turn) => (
              <TurnView
                key={turn.id}
                turn={turn}
                onEntity={onEntity}
                onEntityHover={onEntityHover}
                entityColor={entityColor}
                onShowReferences={onShowReferences}
                showReferencesLabel={showReferencesLabel}
                onStop={stream.stop}
                onRetry={() => retry(turn)}
              />
            ))}
          </ol>
        </div>

        <form
          className="flex-shrink-0 border-t border-neutral-200/70 p-2.5"
          onSubmit={(event) => {
            event.preventDefault();
            if (running) stream.stop();
            else ask(draft);
          }}
        >
          <div className="flex items-end gap-1.5 rounded-2xl bg-neutral-100 py-1 pl-3 pr-1 transition-shadow focus-within:bg-white focus-within:ring-2 focus-within:ring-primary-600/25">
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  if (!running) ask(draft);
                }
              }}
              placeholder={placeholder}
              aria-label={placeholder}
              className="block max-h-[120px] min-h-[32px] flex-1 resize-none border-0 bg-transparent px-0 py-1.5 text-[16px] leading-snug text-ink placeholder:text-neutral-400 focus:outline-none focus:ring-0 sm:text-[13px]"
            />
            <button
              type="submit"
              aria-label={running ? 'Stop' : 'Send'}
              title={running ? 'Stop' : 'Send'}
              disabled={!running && !draft.trim()}
              className="mb-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-ink text-white transition-colors hover:bg-neutral-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:bg-neutral-200 disabled:text-neutral-400"
            >
              {running ? <Square className="h-3 w-3 fill-current" strokeWidth={1.75} /> : <ArrowUp className="h-4 w-4" strokeWidth={2} />}
            </button>
          </div>
        </form>
      </section>
    );
  },
);
AskPanel.displayName = 'AskPanel';

const TurnView: React.FC<{
  turn: AskTurn;
  onEntity?: (id: string) => void;
  onEntityHover?: (id: string | null) => void;
  entityColor?: (id: string) => string | undefined;
  onShowReferences?: (ids: string[]) => void;
  showReferencesLabel: string;
  onStop: () => void;
  onRetry: () => void;
}> = ({ turn, onEntity, onEntityHover, entityColor, onShowReferences, showReferencesLabel, onStop, onRetry }) => {
  const { copied, copy } = useCopy();
  const running = turn.status === 'running';
  const failed = turn.status === 'error' || turn.status === 'unavailable';
  return (
    <li className="animate-fade-up motion-reduce:animate-none">
      <p className="mb-2 rounded-2xl bg-neutral-100 px-3 py-2 text-[13px] leading-snug text-ink">{turn.question}</p>
      {running && !turn.text && <StreamProgress lines={turn.progress} running className="px-1" />}
      {turn.text && (
        <div className="px-1">
          <IntelText
            text={turn.text}
            streaming={running}
            onEntity={onEntity}
            onEntityHover={onEntityHover}
            entityColor={entityColor}
          />
        </div>
      )}
      {turn.status === 'done' && !turn.text && <p className="px-1 text-[12px] text-neutral-400">Stopped</p>}
      {failed && <AiNotice status={turn.status} error={turn.error} onRetry={onRetry} className="mt-1" />}
      {(running || turn.text) && (
        <div className="mt-1.5 flex items-center gap-0.5 text-neutral-400">
          {running ? (
            <button
              type="button"
              onClick={onStop}
              className="inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-[12px] transition-colors hover:bg-neutral-100 hover:text-ink"
            >
              <Square className="h-2.5 w-2.5 fill-current" strokeWidth={1.75} />
              Stop
            </button>
          ) : (
            <>
              {onShowReferences && turn.entityIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => onShowReferences(turn.entityIds)}
                  className="inline-flex h-7 items-center gap-1.5 rounded-full px-2 text-[12px] text-neutral-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                  <Crosshair className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {showReferencesLabel}
                  <span className="tabular-nums text-neutral-400">{turn.entityIds.length}</span>
                </button>
              )}
              <IconButton label={copied ? 'Copied' : 'Copy answer'} size="xs" onClick={() => copy(plainText(turn.text))}>
                {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />}
              </IconButton>
              <IconButton label="Ask again" size="xs" onClick={onRetry}>
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.75} />
              </IconButton>
            </>
          )}
        </div>
      )}
    </li>
  );
};

export default AskPanel;
